import React, { createContext, useReducer, Reducer } from 'react';
import { noop } from 'common/utils';
import type { Faceplate } from 'common/api/misc';
import { addToCart } from './add-to-cart';
import { useNavMenu } from 'components/nav/context';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updateCart } from 'common/features/store/duck/cart/duck';
import { useFormikContext } from 'formik';
import { ProductAmountInputFormData } from 'features/product/components/ProductAmountInput';
import { ModalControls } from 'hooks/useModalControls';

const initialState = {
    step: 1,
    selectedCardDesign: null,
    deliveryDate: new Date(),
    senderName: undefined,
    message: '',
    recipients: [],
    giftImageOptions: [],
    productId: null,
    addingToCartLoading: false,
};

export interface Recipient {
    name: string;
    email: string;
}

export interface FileDetails {
    name: string;
    size: number;
}

export interface BulkOrderState {
    step: number;
    selectedCardDesign: Faceplate | null;
    deliveryDate: Date | null;
    senderName: string | undefined;
    message: string;
    recipients: Recipient[];
    giftImageOptions: Faceplate[];
    productId: number | null;
    addingToCartLoading: boolean;
    fileDetails?: FileDetails;
}

export const actions = {
    SET_STEP: 'SET_STEP',
    SET_SELECTED_CARD_DESIGN: 'SET_SELECTED_CARD_DESIGN',
    SET_DELIVERY_DATE: 'SET_DELIVERY_DATE',
    SET_SENDER_NAME: 'SET_SENDER_NAME',
    SET_MESSAGE: 'SET_MESSAGE',
    SET_RECIPIENTS: 'SET_RECIPIENTS',
    DELETE_RECIPIENTS: 'DELETE_RECIPIENTS',
    SET_GIFT_IMAGE_OPTIONS: 'SET_GIFT_IMAGE_OPTIONS',
    SET_PRODUCT_ID: 'SET_PRODUCT_ID',
    SET_ADDING_TO_CART_LOADING: 'SET_ADDING_TO_CART_LOADING',
    SET_FILE_DETAILS: 'SET_FILE_DETAILS',
};

type Payloads =
    | Faceplate
    | Date
    | string
    | Recipient[]
    | Faceplate[]
    | number
    | boolean
    | FileDetails
    | undefined;
type Action<T> = {
    type: string;
    payload?: T;
};
export const createAction =
    <T extends Payloads>(type: string) =>
    (payload: T): Action<T> => ({ type, payload });

export const setStep = createAction<number>(actions.SET_STEP);
export const setSelectedCardDesign = createAction<Faceplate>(actions.SET_SELECTED_CARD_DESIGN);
export const setDeliveryDate = createAction<Date>(actions.SET_DELIVERY_DATE);
export const setSenderName = createAction<string>(actions.SET_SENDER_NAME);
export const setMessage = createAction<string>(actions.SET_MESSAGE);
export const setRecipients = createAction<Recipient[]>(actions.SET_RECIPIENTS);
export const setGiftImageOptions = createAction<Faceplate[]>(actions.SET_GIFT_IMAGE_OPTIONS);
export const deleteRecipients = () => ({ type: actions.DELETE_RECIPIENTS });
export const setProductId = createAction<number>(actions.SET_PRODUCT_ID);
export const setAddingToCartLoading = createAction<boolean>(actions.SET_ADDING_TO_CART_LOADING);
export const setFileDetails = createAction<FileDetails | undefined>(actions.SET_FILE_DETAILS);

export const reducer = (state: BulkOrderState, action: Action<Payloads>): BulkOrderState => {
    switch (action.type) {
        case actions.SET_STEP:
            return { ...state, step: action.payload as number };
        case actions.SET_SELECTED_CARD_DESIGN:
            return { ...state, selectedCardDesign: action.payload as Faceplate };
        case actions.SET_DELIVERY_DATE:
            return { ...state, deliveryDate: action.payload as Date };
        case actions.SET_SENDER_NAME:
            return { ...state, senderName: action.payload as string };
        case actions.SET_MESSAGE:
            return { ...state, message: action.payload as string };
        case actions.SET_RECIPIENTS:
            return { ...state, recipients: action.payload as Recipient[] };
        case actions.SET_GIFT_IMAGE_OPTIONS:
            return { ...state, giftImageOptions: action.payload as Faceplate[] };
        case actions.DELETE_RECIPIENTS:
            return { ...state, recipients: [] };
        case actions.SET_PRODUCT_ID:
            return { ...state, productId: action.payload as number };
        case actions.SET_ADDING_TO_CART_LOADING:
            return { ...state, addingToCartLoading: action.payload as boolean };
        case actions.SET_FILE_DETAILS:
            return { ...state, fileDetails: action.payload as FileDetails };
        default:
            return state;
    }
};

/* eslint-disable */
type ContextType = {
    state: BulkOrderState;
    setStep: (step: number) => any;
    setSelectedCardDesign: (design: Faceplate) => any;
    setDeliveryDate: (date: Date) => any;
    setSenderName: (name: string) => any;
    setMessage: (message: string) => any;
    setRecipients: (recipients: Recipient[]) => any;
    setGiftImageOptions: (options: Faceplate[]) => any;
    deleteRecipients: () => any;
    setProductId: (id: number) => any;
    addToCart: () => Promise<void>;
    setFileDetails: (fileDetails: FileDetails | undefined) => void;
};
/* eslint-enable */
export const BulkOrderContext = createContext<ContextType>({
    state: initialState,
    setStep: noop,
    setSelectedCardDesign: noop,
    setDeliveryDate: noop,
    setSenderName: noop,
    setMessage: noop,
    setRecipients: noop,
    setGiftImageOptions: noop,
    deleteRecipients: noop,
    setProductId: noop,
    addToCart: () => Promise.resolve(),
    setFileDetails: noop,
});

type BulkOrderProviderProps = {
    children: React.ReactNode;
    closeGiftDetails: () => void;
    errorModalControls: ModalControls;
};

export const BulkOrderProvider = ({
    children,
    closeGiftDetails,
    errorModalControls,
}: BulkOrderProviderProps) => {
    const [state, dispatch] = useReducer<Reducer<BulkOrderState, Action<Payloads>>>(
        reducer,
        initialState
    );
    const history = useHistory();
    const { setOpenMenu } = useNavMenu();
    const formikCtx = useFormikContext<ProductAmountInputFormData>();

    const dispatchRedux = useDispatch();

    const amount = {
        priceOverride: formikCtx.values.priceOverride || null,
        selectedOptions: formikCtx.values.selectedOptions,
    };

    const boundActions = {
        setStep: (step: number) => dispatch(setStep(step)),
        setSelectedCardDesign: (design: Faceplate) => dispatch(setSelectedCardDesign(design)),
        setDeliveryDate: (date: Date) => dispatch(setDeliveryDate(date)),
        setSenderName: (name: string) => dispatch(setSenderName(name)),
        setMessage: (message: string) => dispatch(setMessage(message)),
        setRecipients: (recipients: Recipient[]) => dispatch(setRecipients(recipients)),
        setFileDetails: (fileDetails: FileDetails | undefined) =>
            dispatch(setFileDetails(fileDetails)),
        setGiftImageOptions: (options: Faceplate[]) => dispatch(setGiftImageOptions(options)),
        deleteRecipients: () => dispatch(deleteRecipients()),
        setProductId: (id: number) => dispatch(setProductId(id)),
        addToCart: async () => {
            dispatch(setAddingToCartLoading(true));

            if (state.productId) {
                const response = await addToCart(state, state?.productId, amount);
                if (response.data) {
                    dispatchRedux(updateCart({ cart: response.data }));
                    dispatch(setAddingToCartLoading(false));
                    setOpenMenu('cart');
                    closeGiftDetails();
                    history.goBack();
                } else {
                    errorModalControls.toggleModalVisibility();
                }
            }
        },
    };

    const value = {
        state: state,
        ...boundActions,
    };

    return <BulkOrderContext.Provider value={value}>{children}</BulkOrderContext.Provider>;
};
